import { EffectiveEmployeeStatus, ExternalSource, type NurseFullRole } from "../../utils/models";
import { TEAM_SECTIONS } from "./config";
import { TEAM_NEEDS_DECISION_PARAM, TEAM_NEEDS_DECISION_VALUE } from "./statusDecisions/deepLink";
import type { ActiveStatFilter, StatFilterKey } from "./TeamStats";
import {
	AUTO_APPROVE_ELIGIBLE_ID,
	AUTO_APPROVE_NON_ELIGIBLE_ID,
	DEFAULT_SELECTED_STATUSES,
	HAS_EMAIL_FILTER_ID,
	HAS_PHONE_FILTER_ID,
	LAST_LOGIN_BUCKET_IDS,
	NO_AGENCY_FILTER_ID,
	NO_EMAIL_FILTER_ID,
	NO_INTEGRATOR_FILTER_ID,
	NO_PHONE_FILTER_ID,
} from "./teamFacetFilters";
import { HOLIDAY_DAY_PART_FILTER_IDS } from "./teamHolidayFacets";

/**
 * The Team page's filter state lives in the URL, and only in the URL.
 *
 * Two reasons, and they pull the same way. The page used to seed itself from a localStorage blob
 * per (user, department), which meant walking back into Team gave you whatever you had left set
 * days ago — including, by default, a filter hiding every agency employee. APP-609 asks for the
 * opposite: always come back to the default state. And putting the state in the query string is
 * what makes a filtered roster shareable and the browser Back button undo a filter, which the page
 * has never been able to do.
 *
 * So: absent parameter = the default. Never "what you did last time".
 *
 * Values are repeated parameters (`?agency=Maxim&agency=Aya`) rather than a comma-joined list,
 * because agency and union names are free text and a comma inside one would corrupt the list.
 */

export const TEAM_PARAM = {
	SEARCH: "q",
	ROLE: "role",
	JOB: "job",
	CONTRACT: "contract",
	STATUS: "status",
	AGENCY: "agency",
	UNION: "union",
	LAST_LOGIN: "last_login",
	EMAIL: "email",
	PHONE: "phone",
	AUTO_APPROVE: "auto_approve",
	INTEGRATOR: "integrator",
	SEGMENT: "segment",
	SEGMENT_STATE: "segment_state",
	HOLIDAY_GROUP: "holiday_group",
	HOLIDAY_DAY_PART: "holiday_day_part",
	/** Pre-existing deep link from the Employee Status Review notification. */
	NEEDS_DECISION: TEAM_NEEDS_DECISION_PARAM,
} as const;

/** Every Team parameter, so a reset can strip them without disturbing anything else on the URL. */
const ALL_TEAM_PARAMS: readonly string[] = Object.values(TEAM_PARAM);

export type TeamUrlState = {
	searchQuery: string;
	selectedRoles: NurseFullRole[];
	selectedJobNames: string[];
	selectedSections: Set<string>;
	selectedStatuses: Set<string>;
	selectedAgencies: Set<string>;
	selectedUnions: Set<string>;
	selectedLastLogin: Set<string>;
	selectedEmail: Set<string>;
	selectedPhone: Set<string>;
	selectedAutoApprove: Set<string>;
	selectedIntegrators: Set<string>;
	/** Holiday group ids — narrowed to the department's groups by `useTeamFilters`, like jobs. */
	selectedHolidayGroups: Set<string>;
	selectedHolidayDayParts: Set<string>;
	activeStatFilter: ActiveStatFilter;
	showOnlyDecisions: boolean;
};

const STAT_FILTER_KEYS: readonly StatFilterKey[] = ["workedLastMonth", "neverLoggedIn", "recurringShifts", "selfScheduling"];

/**
 * The closed vocabularies — the only ids each of these dimensions can ever mean. Anything else in
 * the URL is stale (a renamed value, a hand-typed link) and is dropped by `readKnownSet`.
 *
 * Roles, jobs, agencies and unions are deliberately absent: those merge an unknown checked id back
 * into their own options (see `buildSourceListOptions`), so a stale value there stays clearable.
 */
const KNOWN_STATUSES: ReadonlySet<string> = new Set(Object.values(EffectiveEmployeeStatus));
const KNOWN_SECTIONS: ReadonlySet<string> = new Set(TEAM_SECTIONS.map((section) => section.title));
const KNOWN_LAST_LOGIN: ReadonlySet<string> = new Set(LAST_LOGIN_BUCKET_IDS);
const KNOWN_EMAIL: ReadonlySet<string> = new Set([HAS_EMAIL_FILTER_ID, NO_EMAIL_FILTER_ID]);
const KNOWN_PHONE: ReadonlySet<string> = new Set([HAS_PHONE_FILTER_ID, NO_PHONE_FILTER_ID]);
const KNOWN_AUTO_APPROVE: ReadonlySet<string> = new Set([AUTO_APPROVE_ELIGIBLE_ID, AUTO_APPROVE_NON_ELIGIBLE_ID]);
const KNOWN_INTEGRATORS: ReadonlySet<string> = new Set<string>([...Object.values(ExternalSource), NO_INTEGRATOR_FILTER_ID]);
const KNOWN_HOLIDAY_DAY_PARTS: ReadonlySet<string> = new Set(HOLIDAY_DAY_PART_FILTER_IDS);

/**
 * Reads a repeated parameter as a set. Free-text vocabularies only (agency and union names) —
 * closed ones go through `readKnownSet`.
 *
 * The three states are deliberate and all reachable: parameter absent → `fallback` (the page
 * default); parameter present with values → those values; parameter present and empty (`?agency=`)
 * → the empty set, which every facet reads as "unrestricted". Without that third state there would
 * be no way to express "show me agency staff too" in a URL, since absent already means "hide them".
 */
function readSet(params: URLSearchParams, key: string, fallback: readonly string[] = []): Set<string> {
	if (!params.has(key)) return new Set(fallback);
	return new Set(params.getAll(key).filter(Boolean));
}

/**
 * `readSet` for a closed vocabulary: an unrecognized value would filter the roster to zero rows with
 * nothing to untick — the same trap `validSegment` and `isSelectableJobName` already avoid. Worst for
 * integrator, email, phone and auto-approve, which are URL-only and have no rail group at all.
 *
 * Unknown values are dropped. A parameter whose values are *all* unknown falls back to `fallback`
 * rather than to the empty set, because empty means "unrestricted" and would silently show more than
 * the link asked for. An explicitly-empty parameter (`?status=`) still means "unrestricted".
 */
function readKnownSet(params: URLSearchParams, key: string, known: ReadonlySet<string>, fallback: readonly string[] = []): Set<string> {
	if (!params.has(key)) return new Set(fallback);
	const requested = params.getAll(key).filter(Boolean);
	if (requested.length === 0) return new Set();
	const valid = requested.filter((value) => known.has(value));
	return valid.length > 0 ? new Set(valid) : new Set(fallback);
}

export function parseTeamUrlState(params: URLSearchParams): TeamUrlState {
	const segmentKey = params.get(TEAM_PARAM.SEGMENT);
	const segmentState = params.get(TEAM_PARAM.SEGMENT_STATE);
	// An unknown segment (a stale link after a card was renamed) is dropped rather than applied, so
	// a bad URL can't strand the roster on a filter with no matching card to clear it.
	const validSegment = STAT_FILTER_KEYS.find((key) => key === segmentKey);
	// Absence means the documented default ("matching"). A value that is neither known state is a
	// malformed link, and it drops the segment filter entirely — the same treatment an unknown
	// `segment` gets above, and for the same reason: a silent narrowing no card can explain is worse
	// than no narrowing at all.
	const isKnownSegmentState = segmentState === null || segmentState === "matching" || segmentState === "not-matching";
	const showOnlyDecisions = params.get(TEAM_PARAM.NEEDS_DECISION) === TEAM_NEEDS_DECISION_VALUE;
	/**
	 * The one place a parameter's absence does NOT mean the page default.
	 *
	 * Decisions mode bypasses the status facets but NOT the standard ones, and Agency is the only
	 * standard facet whose default actually restricts (`{NO_AGENCY_FILTER_ID}` = hide agency staff).
	 * So a bare `?needs_decision=true` — which is exactly what the Employee Status Review
	 * notification sends — would land the user on a roster with every agency employee awaiting a
	 * decision filtered out, while the banner counting them still includes them.
	 *
	 * Resolved here rather than by writing `&agency=` into the link, because links already sent
	 * carry only the one parameter. An explicit `?agency=…` (or `?agency=`) still wins: the
	 * exception is for absence, so the user can still narrow by agency once they arrive.
	 */
	const agencyFallback = showOnlyDecisions ? [] : [NO_AGENCY_FILTER_ID];

	return {
		searchQuery: params.get(TEAM_PARAM.SEARCH) ?? "",
		selectedRoles: params.getAll(TEAM_PARAM.ROLE).filter(Boolean) as NurseFullRole[],
		selectedJobNames: params.getAll(TEAM_PARAM.JOB).filter(Boolean),
		selectedSections: readKnownSet(params, TEAM_PARAM.CONTRACT, KNOWN_SECTIONS),
		selectedStatuses: readKnownSet(params, TEAM_PARAM.STATUS, KNOWN_STATUSES, DEFAULT_SELECTED_STATUSES),
		selectedAgencies: readSet(params, TEAM_PARAM.AGENCY, agencyFallback),
		// Lower-cased to match `nurseUnionKey`, which folds case so one union is one option. Done here
		// rather than per-nurse so a hand-typed `?union=UFCW` still selects the facet.
		selectedUnions: new Set([...readSet(params, TEAM_PARAM.UNION)].map((value) => value.toLowerCase())),
		selectedLastLogin: readKnownSet(params, TEAM_PARAM.LAST_LOGIN, KNOWN_LAST_LOGIN),
		selectedEmail: readKnownSet(params, TEAM_PARAM.EMAIL, KNOWN_EMAIL),
		selectedPhone: readKnownSet(params, TEAM_PARAM.PHONE, KNOWN_PHONE),
		selectedAutoApprove: readKnownSet(params, TEAM_PARAM.AUTO_APPROVE, KNOWN_AUTO_APPROVE),
		selectedIntegrators: readKnownSet(params, TEAM_PARAM.INTEGRATOR, KNOWN_INTEGRATORS),
		selectedHolidayGroups: readSet(params, TEAM_PARAM.HOLIDAY_GROUP),
		selectedHolidayDayParts: readKnownSet(params, TEAM_PARAM.HOLIDAY_DAY_PART, KNOWN_HOLIDAY_DAY_PARTS),
		activeStatFilter: validSegment && isKnownSegmentState ? { key: validSegment, state: segmentState === "not-matching" ? "not-matching" : "matching" } : null,
		showOnlyDecisions,
	};
}

/** Writes a set, omitting it when it equals the default so a default page carries a clean URL. */
function writeSet(params: URLSearchParams, key: string, values: ReadonlySet<string>, fallback: readonly string[] = []): void {
	params.delete(key);
	const isDefault = values.size === fallback.length && fallback.every((value) => values.has(value));
	if (isDefault) return;
	// An explicitly-empty selection still has to be written, or it would read back as the default.
	if (values.size === 0) {
		params.append(key, "");
		return;
	}
	for (const value of values) params.append(key, value);
}

/**
 * Serializes state onto a copy of the current parameters — preserving anything that isn't ours, so
 * a filter change never drops an unrelated query parameter.
 */
export function writeTeamUrlState(current: URLSearchParams, state: TeamUrlState): URLSearchParams {
	const params = new URLSearchParams(current);

	// Whitespace-only is "no search" (same rule as `buildTeamPredicates`), so it must not persist a
	// phantom `q`. The raw value is what gets written when there IS a query — trimming it would strip
	// the trailing space out of the field mid-word while someone types "John Smith".
	if (state.searchQuery.trim()) params.set(TEAM_PARAM.SEARCH, state.searchQuery);
	else params.delete(TEAM_PARAM.SEARCH);

	writeSet(params, TEAM_PARAM.ROLE, new Set(state.selectedRoles));
	writeSet(params, TEAM_PARAM.JOB, new Set(state.selectedJobNames));
	writeSet(params, TEAM_PARAM.CONTRACT, state.selectedSections);
	writeSet(params, TEAM_PARAM.STATUS, state.selectedStatuses, DEFAULT_SELECTED_STATUSES);
	// Same decisions-mode exception as `parseTeamUrlState`, and for the same reason: the two must
	// agree on what Agency's default is, or serializing the state we just parsed would append a
	// redundant `?agency=` and the URL would stop round-tripping cleanly.
	writeSet(params, TEAM_PARAM.AGENCY, state.selectedAgencies, state.showOnlyDecisions ? [] : [NO_AGENCY_FILTER_ID]);
	writeSet(params, TEAM_PARAM.UNION, state.selectedUnions);
	writeSet(params, TEAM_PARAM.LAST_LOGIN, state.selectedLastLogin);
	writeSet(params, TEAM_PARAM.EMAIL, state.selectedEmail);
	writeSet(params, TEAM_PARAM.PHONE, state.selectedPhone);
	writeSet(params, TEAM_PARAM.AUTO_APPROVE, state.selectedAutoApprove);
	writeSet(params, TEAM_PARAM.INTEGRATOR, state.selectedIntegrators);
	writeSet(params, TEAM_PARAM.HOLIDAY_GROUP, state.selectedHolidayGroups);
	writeSet(params, TEAM_PARAM.HOLIDAY_DAY_PART, state.selectedHolidayDayParts);

	if (state.activeStatFilter) {
		params.set(TEAM_PARAM.SEGMENT, state.activeStatFilter.key);
		params.set(TEAM_PARAM.SEGMENT_STATE, state.activeStatFilter.state);
	} else {
		params.delete(TEAM_PARAM.SEGMENT);
		params.delete(TEAM_PARAM.SEGMENT_STATE);
	}

	if (state.showOnlyDecisions) params.set(TEAM_PARAM.NEEDS_DECISION, TEAM_NEEDS_DECISION_VALUE);
	else params.delete(TEAM_PARAM.NEEDS_DECISION);

	return params;
}

/** Strips every Team filter parameter — "Clear all" and the Total-staff card's reset. */
export function clearTeamUrlState(current: URLSearchParams): URLSearchParams {
	const params = new URLSearchParams(current);
	for (const key of ALL_TEAM_PARAMS) params.delete(key);
	return params;
}

/**
 * The stat cards' tri-state cycle: matching → not-matching → off.
 *
 * Clicking a *different* card starts that card's cycle from the beginning rather than advancing the
 * previous one — only one segment can be applied at a time, so inheriting the old card's state would
 * silently invert the new one on the first click.
 */
export function nextStatFilter(previous: ActiveStatFilter, key: StatFilterKey): ActiveStatFilter {
	if (previous?.key !== key) return { key, state: "matching" };
	return previous.state === "matching" ? { key, state: "not-matching" } : null;
}

/**
 * How many dimensions "Clear all" would actually change — the number on the rail's badge.
 *
 * A dimension is "active" exactly when clearing would move it, so a fresh page in its default state
 * (Active status, agency staff hidden, no segment) counts zero. It lives beside the parser because
 * "is this dimension at its default?" is the same question `writeSet` asks when it decides to omit a
 * parameter, and the two must not drift.
 *
 * `state.selectedJobNames` must already be narrowed to the department's enabled jobs: a URL naming a
 * retired job filters nothing, so it must not count either.
 */
export function countActiveTeamFilters(
	state: TeamUrlState,
	{ autoApproveEnabled, statFilterApplies }: { autoApproveEnabled: boolean; statFilterApplies: boolean },
): number {
	const isDefaultStatuses =
		state.selectedStatuses.size === DEFAULT_SELECTED_STATUSES.length && DEFAULT_SELECTED_STATUSES.every((status) => state.selectedStatuses.has(status));
	// Same default the parser and serializer use, decisions mode included — there the default is the
	// EMPTY set, so a bare `?needs_decision=true` link would otherwise badge an Agency filter the user
	// never chose and Clear All would offer to undo nothing.
	const agencyDefault = state.showOnlyDecisions ? [] : [NO_AGENCY_FILTER_ID];
	const isDefaultAgencies = state.selectedAgencies.size === agencyDefault.length && agencyDefault.every((id) => state.selectedAgencies.has(id));
	const flags = [
		// Trimmed, to agree with `buildTeamPredicates`: a whitespace-only query filters nothing, so
		// counting it would put a "1" on the badge with no row change to explain it.
		state.searchQuery.trim().length > 0,
		state.selectedRoles.length > 0,
		state.selectedJobNames.length > 0,
		state.selectedSections.size > 0,
		!isDefaultStatuses,
		// Only when the segment can actually filter: a card the department doesn't render resolves to no
		// predicate (see `activeStatFilterIdSet`), so counting it badges a filter with nothing to clear.
		state.activeStatFilter !== null && statFilterApplies,
		state.showOnlyDecisions,
		!isDefaultAgencies,
		state.selectedUnions.size > 0,
		state.selectedLastLogin.size > 0,
		state.selectedEmail.size > 0,
		state.selectedPhone.size > 0,
		// Only counts when the flag exposes the facet — a hidden, unclearable facet must not inflate
		// the badge or "Clear all".
		autoApproveEnabled && state.selectedAutoApprove.size > 0,
		state.selectedIntegrators.size > 0,
		state.selectedHolidayGroups.size > 0,
		state.selectedHolidayDayParts.size > 0,
	];
	return flags.reduce((count, active) => count + (active ? 1 : 0), 0);
}
